import { useMemo } from 'react';
import { useTheme } from '@emotion/react';
import { Browser, Link, MagicWand, ToggleRight } from '@phosphor-icons/react';
import { toTitleCase } from 'common/commonUtil';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { produceState } from 'client/state/stateUtils';
import { Banner } from 'client/components/Banner';
import { Checkbox } from 'client/components/Checkbox';
import { Collapsible } from 'client/components/Collapsible';
import { Divider } from 'client/components/Divider';
import { PostfixedInput } from 'client/components/Input';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { Stack } from 'client/components/Stack';
import { Toggle } from 'client/components/Toggle';
import { ComponentInputLabel } from './ComponentInputLabel';

export const SettingsHosts = () => {
  const theme = useTheme();
  const allowEnableFromURL = useDeploymentClientState((state) => state.document?.allowEnableFromURL || false);
  const componentsWithValues = useDeploymentClientState((state) => state.document?.componentsWithValues || []);
  const hostValues = useMemo(
    () =>
      componentsWithValues.flatMap((component) =>
        component.hosts.map((host) => ({
          ...host,
          stackComponentID: component.stackComponentID,
          componentName: component.name,
          componentPackageUrl: component.packageUrl,
        }))
      ),
    [componentsWithValues]
  );

  const primaryHost: (typeof hostValues)[number] | null = (hostValues || [])[0] || null;
  const primaryHostDefaultUrl = primaryHost?.defaultDomain || 'diploi.app';

  const isDefaultUsed = !(hostValues || []).some((host) => host.tweakedDomain !== null || host.customDomain !== null);
  const isCustomUsed = (hostValues || []).some((host) => host.customDomain !== null);

  const handleUseDefaultToggle = produceState(useDeploymentClientState, (state, checked: boolean) => {
    const hosts = state.document.componentsWithValues.flatMap((component) => component.hosts);

    if (checked) {
      for (const host of hosts) {
        host.tweakedDomain = null;
        host.customDomain = null;
      }
    } else {
      // TODO: Cache?
      for (const host of hosts) {
        host.tweakedDomain = host.defaultDomain;
        host.customDomain = null;
      }
    }
  });

  const handleCustomDomainToggle = produceState(
    useDeploymentClientState,
    (state, stackComponentID: number, hostIdentifier: string, checked: boolean) => {
      const host = state.document.componentsWithValues
        .find((component) => component.stackComponentID === stackComponentID)
        ?.hosts.find((host) => host.identifier === hostIdentifier);
      if (!host) return;

      if (checked) {
        host.tweakedDomain = null;
        host.customDomain = '';
      } else {
        // TODO: Cache?
        host.tweakedDomain = host.defaultDomain || '';
        host.customDomain = null;
      }
    }
  );

  const handleDomainChange = produceState(
    useDeploymentClientState,
    (state, stackComponentID: number, hostIdentifier: string, value: string) => {
      const host = state.document.componentsWithValues
        .find((component) => component.stackComponentID === stackComponentID)
        ?.hosts.find((host) => host.identifier === hostIdentifier);
      if (!host) return;

      if (host.customDomain !== null) {
        host.customDomain = value;
      } else if (host.tweakedDomain !== null) {
        host.tweakedDomain = value;
      }
    }
  );

  const handleChangeEnableFromURL = produceState(useDeploymentClientState, (state, enabled: boolean) => {
    state.document.allowEnableFromURL = enabled;
  });

  return (
    <Stack>
      <Toggle
        title="Use Auto-Generated Endpoints"
        subtitle={
          <>
            <Link weight="bold" />
            {primaryHostDefaultUrl}
          </>
        }
        checked={isDefaultUsed}
        icon={<MagicWand color={theme.color.grey[100]} />}
        onChange={handleUseDefaultToggle}
      />

      <Collapsible open={!isDefaultUsed} style={{ marginTop: -16 }}>
        <Divider />

        <Stack>
          {hostValues.map((host, index) => {
            if (!host.defaultDomain) return null;

            const label = toTitleCase(host.identifier);
            const defaultDomainSlug = host.defaultDomain.split('.')[0] || '';
            const tweakedDomainRoot = `.${host.defaultDomain.split('.').slice(1).join('.')}`;
            const tweakedDomainSlug = (host.tweakedDomain || '').replace(tweakedDomainRoot, '');
            const customDomain = host.customDomain;

            const isCustomDomain = customDomain !== null;

            return (
              <Stack key={host.stackComponentID + host.identifier} gap={8}>
                <PostfixedInput
                  path={`configuration.hosts.${index}`}
                  label={
                    <ComponentInputLabel name={label} componentName={host.componentName} packageUrl={host.componentPackageUrl || ''} />
                  }
                  postfix={isCustomDomain ? '' : tweakedDomainRoot}
                  placeholder={isCustomDomain ? 'my.domain.com' : ''}
                  value={customDomain ?? tweakedDomainSlug ?? defaultDomainSlug}
                  onChange={(event) =>
                    handleDomainChange(
                      host.stackComponentID,
                      host.identifier,
                      isCustomDomain ? event.target.value : `${event.target.value}${tweakedDomainRoot}`
                    )
                  }
                />
                <Checkbox
                  value={isCustomDomain}
                  onChange={(checked) => handleCustomDomainToggle(host.stackComponentID, host.identifier, checked)}
                >
                  Use Custom Domain
                </Checkbox>
              </Stack>
            );
          })}

          {isCustomUsed && (
            <Banner>
              Please be aware that custom domains are not officially supported at this time. If you wish to use them, please reach out to
              our support team for instructions.
            </Banner>
          )}
        </Stack>
      </Collapsible>

      <Divider plain />

      <Toggle
        title="Allow Enabling Deployment from its Endpoints"
        subtitle="Show an enable toggle on endpoints when deployment is disabled"
        checked={allowEnableFromURL}
        icon={<ListItemFigure icon={<Browser />} ornament={<ToggleRight />} />}
        onChange={handleChangeEnableFromURL}
      />
    </Stack>
  );
};
